import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useRouteError } from "react-router";
import { copyTextToClipboard } from "../../utils/copyToClipboard";

// Styled inline (not with the app's SCSS modules/Button component) on purpose:
// the crash screen must not depend on anything that could itself be broken.
// Colours come from the `--pf-*` tokens (tokens.css is imported first, before
// any app code) so the crash screen follows the light/dark theme.
const styles: Record<string, React.CSSProperties> = {
	page: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		minHeight: "100vh",
		padding: "48px 20px",
		background: "var(--pf-surface)",
		fontFamily: "'Inter', sans-serif",
		textAlign: "center",
	},
	title: {
		fontSize: "20px",
		fontWeight: 500,
		lineHeight: "30px",
		color: "var(--pf-fg)",
		margin: "24px 0 4px",
	},
	description: {
		fontSize: "14px",
		lineHeight: "24px",
		color: "var(--pf-fg-muted)",
		margin: "0 0 24px",
		maxWidth: "400px",
	},
	codeCard: {
		width: "100%",
		maxWidth: "640px",
		background: "var(--pf-surface)",
		border: "1px solid var(--pf-border)",
		borderRadius: "8px",
		overflow: "hidden",
		textAlign: "left",
	},
	codeHeader: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: "8px",
		padding: "8px 8px 8px 16px",
		background: "var(--pf-surface-2)",
		borderBottom: "1px solid var(--pf-border)",
	},
	codeFilename: {
		fontSize: "12px",
		fontFamily: "'Fira Code', monospace",
		color: "var(--pf-fg-muted)",
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	copyButton: {
		display: "inline-flex",
		alignItems: "center",
		gap: "6px",
		padding: "5px 10px",
		background: "var(--pf-surface)",
		border: "1px solid var(--pf-border)",
		borderRadius: "6px",
		fontSize: "12px",
		fontWeight: 500,
		color: "var(--pf-fg)",
		cursor: "pointer",
		flexShrink: 0,
		fontFamily: "'Inter', sans-serif",
	},
	codeBody: {
		padding: "16px",
		margin: 0,
		fontSize: "12px",
		fontFamily: "'Fira Code', monospace",
		lineHeight: 1.7,
		maxHeight: "240px",
		overflow: "auto",
		whiteSpace: "pre-wrap",
		wordBreak: "break-word",
	},
	actions: {
		display: "flex",
		gap: "12px",
		alignItems: "center",
		justifyContent: "center",
		flexWrap: "wrap",
		marginTop: "24px",
	},
	primaryButton: {
		display: "inline-block",
		padding: "10px 28px",
		background: "var(--pf-action-bg)",
		border: "1px solid var(--pf-action-bg)",
		borderRadius: "6px",
		fontSize: "13px",
		fontWeight: 600,
		color: "var(--pf-action-fg)",
		textDecoration: "none",
		cursor: "pointer",
		fontFamily: "'Inter', sans-serif",
	},
	secondaryButton: {
		display: "inline-block",
		padding: "10px 28px",
		background: "var(--pf-surface)",
		border: "1px solid var(--pf-accent)",
		borderRadius: "6px",
		fontSize: "13px",
		fontWeight: 600,
		color: "var(--pf-accent)",
		textDecoration: "none",
		cursor: "pointer",
		fontFamily: "'Inter', sans-serif",
	},
	tertiaryButton: {
		display: "inline-block",
		padding: "10px 16px",
		background: "none",
		border: "none",
		fontSize: "13px",
		fontWeight: 500,
		color: "var(--pf-fg-muted)",
		textDecoration: "underline",
		cursor: "pointer",
		fontFamily: "'Inter', sans-serif",
	},
};

/**
 * Minimal broken-page illustration in the theme's neutral greys with the
 * accent only on the warning badge. Inline SVG so the crash screen has zero
 * asset/network dependencies.
 */
const CrashIllustration = (): React.ReactElement => (
	<svg
		width="180"
		height="120"
		viewBox="0 0 180 120"
		fill="none"
		role="img"
		aria-hidden="true"
	>
		{/* browser window */}
		<rect
			x="21"
			y="9"
			width="138"
			height="94"
			rx="8"
			fill="var(--pf-surface)"
			stroke="var(--pf-border-strong)"
			strokeWidth="2"
		/>
		<path d="M21 31h138" stroke="var(--pf-border-strong)" strokeWidth="2" />
		<circle cx="34" cy="20" r="3" fill="var(--pf-border)" />
		<circle cx="45" cy="20" r="3" fill="var(--pf-border)" />
		<circle cx="56" cy="20" r="3" fill="var(--pf-border)" />
		{/* content placeholder lines */}
		<path
			d="M36 47h56M36 61h40M36 75h48"
			stroke="var(--pf-border)"
			strokeWidth="6"
			strokeLinecap="round"
		/>
		{/* warning badge */}
		<circle cx="128" cy="72" r="19" fill="var(--pf-accent)" />
		<path
			d="M128 62v11"
			stroke="var(--pf-accent-fg)"
			strokeWidth="3"
			strokeLinecap="round"
		/>
		<circle cx="128" cy="81" r="2" fill="var(--pf-accent-fg)" />
	</svg>
);

const CopyIcon = (): React.ReactElement => (
	<svg
		width="12"
		height="12"
		viewBox="0 0 24 24"
		fill="none"
		aria-hidden="true"
	>
		<rect
			x="9"
			y="9"
			width="12"
			height="12"
			rx="2"
			stroke="currentColor"
			strokeWidth="2"
		/>
		<path
			d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"
			stroke="currentColor"
			strokeWidth="2"
		/>
	</svg>
);

const CheckIcon = (): React.ReactElement => (
	<svg
		width="12"
		height="12"
		viewBox="0 0 24 24"
		fill="none"
		aria-hidden="true"
	>
		<path
			d="M20 6L9 17l-5-5"
			stroke="currentColor"
			strokeWidth="2.5"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

interface ErrorFallbackViewProps {
	error: unknown;
	componentStack?: string;
	onRetry?: () => void;
}

/**
 * Shared crash screen: illustration, the error as a code block (message +
 * stack) with a "Copy code" action, and Go home / Contact us CTAs.
 *
 * Rendered from two places that can't share a hook context, so it takes the
 * error as a plain prop:
 *  - `ErrorFallback` (React error boundary, OUTSIDE the router)
 *  - `RouteErrorFallback` (react-router `errorElement`, inside the router)
 *
 * Navigation uses plain <a> (not <Link>) on purpose: the dashboard boundary
 * sits above the router, and a hard navigation also remounts the crashed tree.
 */
const ErrorFallbackView = ({
	error,
	componentStack,
	onRetry,
}: ErrorFallbackViewProps): React.ReactElement => {
	const { t } = useTranslation();
	const [copied, setCopied] = useState(false);

	const errorObject = error instanceof Error ? error : undefined;
	const message =
		errorObject?.message ||
		(typeof error === "string" && error) ||
		t("errorFallback.unknownError");
	const errorName = errorObject?.name || "Error";
	const headline = `${errorName}: ${message}`;
	// error.stack usually repeats "Name: message" as its first line — the code
	// block already renders that headline, so strip the duplicate.
	const rawStack = componentStack || errorObject?.stack || "";
	const stack = rawStack.startsWith(headline)
		? rawStack.slice(headline.length).replace(/^\n/, "")
		: rawStack;

	const codeText = [
		headline,
		stack,
		"",
		`${t("errorFallback.url")}: ${window.location.href}`,
		`${t("errorFallback.time")}: ${new Date().toISOString()}`,
		`${t("errorFallback.browser")}: ${navigator.userAgent}`,
	]
		.filter(Boolean)
		.join("\n");

	const copyCode = () => {
		// FRONTEND-71: a permissions policy can block the Clipboard API (embedded
		// checkout iframes); the helper never rejects, and "Copied!" only shows
		// when something was actually copied — the report stays selectable in
		// the code block below either way.
		void copyTextToClipboard(codeText).then((ok) => {
			if (!ok) return;
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		});
	};

	return (
		<div style={styles.page} data-testid="error-fallback">
			<CrashIllustration />

			<h1 style={styles.title}>{t("errorFallback.title")}</h1>
			<p style={styles.description}>{t("errorFallback.description")}</p>

			<div style={styles.codeCard}>
				<div style={styles.codeHeader}>
					<span style={styles.codeFilename}>error.log</span>
					<button
						type="button"
						onClick={copyCode}
						style={styles.copyButton}
						data-testid="copy-code"
					>
						{copied ? <CheckIcon /> : <CopyIcon />}
						{copied ? t("errorFallback.copied") : t("errorFallback.copyCode")}
					</button>
				</div>
				<pre style={styles.codeBody} data-testid="error-code">
					<code>
						<span style={{ color: "var(--pf-danger)" }}>{headline}</span>
						{stack ? (
							<span style={{ color: "var(--pf-fg-muted)" }}>
								{"\n"}
								{stack}
							</span>
						) : null}
					</code>
				</pre>
			</div>

			<div style={styles.actions}>
				<a href="/" style={styles.primaryButton} data-testid="go-home">
					{t("errorFallback.goHome")}
				</a>
				<a
					href="/contact"
					style={styles.secondaryButton}
					data-testid="contact-us"
				>
					{t("errorFallback.contactUs")}
				</a>
				<button
					type="button"
					onClick={onRetry ?? (() => window.location.reload())}
					style={styles.tertiaryButton}
				>
					{t("errorFallback.tryAgain")}
				</button>
			</div>
		</div>
	);
};

/** Fallback for the dashboard error boundary (errors thrown outside the router). */
const ErrorFallback = ({
	error,
	resetError,
	componentStack,
}: {
	error: Error;
	resetError?: () => void;
	componentStack?: string;
	exceptionEvent?: unknown;
}): React.ReactElement => (
	<ErrorFallbackView
		error={error}
		componentStack={componentStack}
		onRetry={resetError}
	/>
);

/**
 * Fallback for the data router's `errorElement` (src/index.tsx). Without it,
 * render errors inside routes are swallowed by react-router's built-in
 * developer error screen and never reach the dashboard error boundary.
 */
export const RouteErrorFallback = (): React.ReactElement => {
	const error = useRouteError();
	return <ErrorFallbackView error={error} />;
};

export default ErrorFallback;
